import React from "react";
import { useNavigate } from "react-router-dom";
import { Grid, Paper } from "@mui/material";
import { Input, Button, notification } from "antd";
import { useToaster } from "@components";
import { useFormik } from "formik";
import * as Yup from "yup";

import { usersLoginPost } from "../../libs/apis";
import { useModulesBackupStore, useRouterStore } from "../../libs/stores";
import { LoadingFullPage } from "./Loading";

import CenefaLogin from "../images/logo-footer.png";
import FondoLogin from "../images/fondo-min.jpg";
import VideoBackground from "../images/bg-login.mp4";
import "../css/login.css";

export const Login = () => {
  const navigate = useNavigate();
  const toaster = useToaster();
  const tokens = localStorage.getItem(process.env.REACT_APP_TOKEN!);
  const setModules = useRouterStore((state) => state.setModules);
  const setModulesBackup = useModulesBackupStore(
    (state) => state.setModulesBackup
  );
  const setFirstName = useRouterStore((state) => state.setFirstName);
  const setProfileId = useRouterStore((state) => state.setProfileId);
  const setClientId = useRouterStore((state) => state.setClientId);
  const setClient = useRouterStore((state) => state.setClient);
  const setTypeDashboardCode = useRouterStore(
    (state) => state.setTypeDashboardCode
  );
  const setAvatar = useRouterStore((state) => state.setAvatar);
  const [isProcessing, setProcessing] = React.useState(false);
  const [videoLoaded, setVideoLoaded] = React.useState(false);
  const [continuo, setContinuo] = React.useState(false);

  React.useEffect(() => {
    // Si el usuario ya está autenticado, redirígelo
    if (tokens) {
      navigate("/dashboard", { replace: true });
    }
  }, [tokens, navigate]);

  React.useEffect(() => {
    if (continuo) {
      setContinuo(false);
      navigate("/dashboard", { replace: true });
    }
  }, [continuo]);

  const openNotification = (message: string) => {
    toaster.success({
      title: "Bienvenido",
      message,
      duration: 5000,
      placement: "topRight",
    });
  };

  const formik = useFormik({
    initialValues: {
      user: "",
      password: "",
    },
    validationSchema: Yup.object({
      user: Yup.string()
        .trim()
        .min(3, "Demasiado corto!")
        .max(30, "Demasiado largo!")
        .required("Requerido"),
      password: Yup.string()
        .trim()
        .min(3, "Demasiado corto!")
        .max(30, "Demasiado largo!")
        .required("Requerido"),
    }),
    onSubmit: async (values) => {
      try {
        setProcessing(true);
        const { data: dataUser } = await usersLoginPost(values);
        if (!dataUser) return;

        localStorage.setItem(
          process.env.REACT_APP_TOKEN!,
          JSON.stringify(dataUser)
        );
        openNotification(dataUser.user.fullName);
        // notification.success({
        //   message: "Bienvenido",
        //   description: dataUser.user.fullName,
        //   duration: 3,
        // });
        setModules(dataUser.user.modules);
        setModulesBackup(dataUser.user.modules);
        setClientId(dataUser.user.clientId);
        setClient(dataUser.user.client);
        setProfileId(dataUser.user.profileId);
        setAvatar(dataUser.user.avatar!);
        setFirstName(dataUser.user.firstName);
        setTypeDashboardCode(dataUser.user.typeDashboardCode!);
        setProcessing(false);
        formik.resetForm();
        setContinuo(true);
      } catch (err: any) {
        setProcessing(false);
        toaster.error({
          title: "Error",
          message: err.toString() ?? "Error, comuníquese con Sistemas",
          duration: 3,
        });
      }
    },
  });

  if (tokens) {
    return <LoadingFullPage />; // O puedes usar un spinner o componente de carga
  }

  return (
      <div className="login-container">
        {/* Imagen de fondo mientras se carga el video */}
        {!videoLoaded && (
          <img
            src={FondoLogin}
            alt="Fondo estático"
            className="video-background"
          />
        )}
        <video
          className="video-background"
          autoPlay
          loop
          muted
          playsInline
          onLoadedData={() => setVideoLoaded(true)}
        >
          <source src={VideoBackground} type="video/mp4" />
          Tu navegador no soporta videos HTML5.
        </video>
        <Grid container justifyContent="center" alignContent="center">
          <Grid item md={3} sm={4} xs={10}>
            <Paper
              elevation={3}
              sx={{
                minHeight: 400,
                background: "rgb(25, 25, 112, 0.60)",
                border: "1px solid yellow",
                boxShadow: "9px 9px 5px -2px rgba(0, 0, 0, 0.59)",
                borderRadius: "10px",
              }}
            >
              <form onSubmit={formik.handleSubmit}>
                <Grid
                  container
                  justifyContent="center"
                  alignContent="flex-start"
                  className="login-sub-root"
                >
                  <Grid item xs={10} className="login-cenefa">
                    <img
                      src={CenefaLogin}
                      width={250}
                      height={240}
                      alt="EXPOBANANA"
                    />
                  </Grid>
                  <Grid item sm={9} xs={11} className="login-texfield">
                    <Input
                      min={3}
                      max={20}
                      name="user"
                      addonBefore={
                        <p style={{ color: "white", width: 70 }}>Usuario</p>
                      }
                      value={formik.values.user}
                      onChange={formik.handleChange}
                      status={
                        formik.touched.user && formik.errors.user ? "error" : ""
                      }
                    />
                    {formik.touched.user && (
                      <p style={{ color: "red" }}>
                        {formik.touched.user && formik.errors.user}
                      </p>
                    )}
                  </Grid>
                  <Grid item sm={9} xs={11} className="login-texfield">
                    <Input.Password
                      min={3}
                      max={30}
                      name="password"
                      addonBefore={
                        <p style={{ color: "white", width: 70 }}>Contraseña</p>
                      }
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      status={
                        formik.touched.password && formik.errors.password
                          ? "error"
                          : ""
                      }
                    />
                    {formik.touched.password && (
                      <p style={{ color: "red" }}>
                        {formik.touched.password && formik.errors.password}
                      </p>
                    )}
                  </Grid>
                  <Grid item sm={9} xs={11} className="login-button">
                    <Button
                      type="default"
                      htmlType="submit"
                      style={{ width: "100%" }}
                      loading={isProcessing}
                      disabled={isProcessing}
                    >
                      Entrar
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>
        </Grid>
      </div>
  );
};
